import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/Ui";
import { fmtDateTime } from "@/lib/time";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";

export default function AdminAudit() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("");

    useEffect(() => {
        api.get("/platform/audit", { params: filter ? { tenant_id: filter } : {} }).then(r => setItems(r.data)).catch(e => toast.error(apiError(e)));
    }, [filter]);

    const tableCtrl = useTableControls(items, {
        accessors: {
            actor: (a) => a.user_email || a.actor_email || "",
            method: (a) => a.method || a.kind || "",
            tenant: (a) => a.tenant_id || "",
        },
    });

    return (
        <>
            <PageHeader title="Audit log" subtitle="Every write across the platform. Tap a row to see metadata."/>
            <div className="mb-6 flex gap-3 items-center flex-wrap">
                <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Filter by tenant:</label>
                <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="ObjectId or blank for all"
                    className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs font-mono w-72"/>
            </div>
            <FilterChip ctrl={tableCtrl} className="mb-3"/>
            {items.length === 0 ? <EmptyState title="No audit entries"/> : (
                <div className="border border-border overflow-x-auto">
                    <table className="min-w-[800px] w-full text-sm">
                        <thead className="bg-muted border-b border-border">
                            <tr className="text-left">
                                <SortableTh field="created_at" ctrl={tableCtrl}>When</SortableTh>
                                <SortableTh field="actor" ctrl={tableCtrl}>Actor</SortableTh>
                                <SortableTh field="method" ctrl={tableCtrl}>Method</SortableTh>
                                <SortableTh field="path" ctrl={tableCtrl}>Path</SortableTh>
                                <SortableTh field="status_code" ctrl={tableCtrl}>Status</SortableTh>
                                <SortableTh field="tenant" ctrl={tableCtrl}>Tenant</SortableTh>
                                <SortableTh field="ip" ctrl={tableCtrl}>IP</SortableTh>
                            </tr>
                        </thead>
                        <tbody>
                            {tableCtrl.rows.map(a => (
                                <tr key={a.id} className="border-b border-border" data-testid={`audit-${a.id}`}>
                                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{fmtDateTime(a.created_at)}</td>
                                    <td className="px-4 py-3">{a.user_email || a.actor_email || "—"}</td>
                                    <td className="px-4 py-3 font-mono text-xs">{a.method || a.kind || "—"}</td>
                                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground truncate max-w-[300px]">{a.path || "—"}</td>
                                    <td className="px-4 py-3 font-mono text-xs">{a.status_code || "—"}</td>
                                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{a.tenant_id?.slice(-6) || "—"}</td>
                                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{a.ip || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}
